import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { borderRadius, color } from '@figma/fpl-tokens/tokens.stylex';
import { Icon24Image, Icon24Text, Icon24Transform3d, Icon24VideoLarge, Icon24Voice } from '@figma/fpl-icons';
import { AssetThumbnail } from '@/components/Common/AssetThumbnail';
import { I18N_KEYS } from '@/language/keys';
import { useResolvedAssets } from '@/state/workflow.state';
import { thumbnailSource } from '@/utils/assetUrls';
import type { ThumbnailSource } from '@/utils/assetUrls';
import type { MediaAsset, TextAsset } from '@/types/api/assets';

const typeIcons = {
  image: Icon24Image,
  video: Icon24VideoLarge,
  text: Icon24Text,
  audio: Icon24Voice,
  '3D': Icon24Transform3d,
};

export function ToolOutputThumbnail({
  asset,
  selected,
  fit = 'cover',
}: {
  asset: MediaAsset | TextAsset;
  selected: boolean;
  fit?: 'contain' | 'cover';
}) {
  const { t } = useTranslation();
  const resolvedAssets = useResolvedAssets();
  let source: ThumbnailSource = { kind: 'none' };
  if (asset.type === 'image' || asset.type === 'video' || asset.type === '3D') {
    source =
      asset.type === 'video' && asset.url && asset.thumbnailUrl === asset.url
        ? { kind: 'firstFrame', src: asset.url }
        : thumbnailSource(asset, resolvedAssets);
  }
  const isImage = asset.type === 'image';
  const hasImageThumbnail = isImage && source.kind !== 'none';
  const Icon = typeIcons[asset.type];
  return (
    <div {...stylex.props(styles.tile, (fit === 'contain' || !hasImageThumbnail) && styles.other)}>
      <div
        {...stylex.props(
          styles.artwork,
          fit === 'cover' && hasImageThumbnail && styles.image,
          !selected && styles.unselectedArtwork,
        )}
      >
        <AssetThumbnail
          source={source}
          alt=""
          draggable={false}
          {...stylex.props(styles.media, fit === 'contain' && styles.contain, asset.type === 'video' && styles.video)}
        />
      </div>
      {!hasImageThumbnail && (
        <div
          role={asset.type === 'video' ? 'img' : undefined}
          aria-label={asset.type === 'video' ? t(I18N_KEYS.GENERAL.FILE_TYPES.VIDEO, { count: 1 }) : undefined}
          {...stylex.props(styles.icon, !selected && styles.unselectedIcon)}
        >
          <Icon {...stylex.props(asset.type === 'video' && styles.videoIcon)} />
        </div>
      )}
    </div>
  );
}

const styles = stylex.create({
  tile: {
    width: '100%',
    height: '100%',
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: borderRadius.medium,
  },
  artwork: { width: '100%', height: '100%' },
  image: { backgroundColor: '#fff' },
  other: {
    backgroundColor: color.bgSecondary,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.border,
    boxSizing: 'border-box',
  },
  unselectedArtwork: { opacity: 0.4 },
  unselectedIcon: { opacity: 0.8 },
  media: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  contain: { objectFit: 'contain' },
  video: { filter: 'brightness(0.85)' },
  videoIcon: {
    '--fpl-icon-color': 'var(--ramp-white-1000)',
  },
  icon: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    color: color.icon,
  },
});
